import PageTitle from '@/components/PageTitle';
import { Link } from '@inertiajs/react';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
    status: 401 | 403 | 404 | 500 | 503;
}

const ErrorPage: React.FC<Props> = ({ status }) => {
    const { t } = useTranslation();

    const errorConfig: Record<
        number,
        { title: string; description: string; icon: string }
    > = {
        401: {
            title: t('error.401.title'),
            description: t('error.401.description'),
            icon: '🔒',
        },
        403: {
            title: t('error.403.title'),
            description: t('error.403.description'),
            icon: '🚫',
        },
        404: {
            title: t('error.404.title'),
            description: t('error.404.description'),
            icon: '🔍',
        },
        500: {
            title: t('error.500.title'),
            description: t('error.500.description'),
            icon: '⚠️',
        },
        503: {
            title: t('error.503.title'),
            description: t('error.503.description'),
            icon: '🔧',
        },
    };

    const config = errorConfig[status] || errorConfig[500];

    return (
        <>
            <PageTitle title={`Error ${status}`} />
            <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 px-4">
                {/* Logo */}
                <div className="mb-8">
                    <img
                        src="/static/img/conquerx_logo.png"
                        alt="ConquerX"
                        className="h-10"
                    />
                </div>

                {/* Error Card */}
                <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-lg">
                    {/* Icon */}
                    <div className="mb-6 text-6xl">{config.icon}</div>

                    {/* Error Code */}
                    <div className="mb-2 text-7xl font-bold text-gray-200">
                        {status}
                    </div>

                    {/* Title */}
                    <h1 className="mb-3 text-2xl font-bold text-gray-900">
                        {config.title}
                    </h1>

                    {/* Description */}
                    <p className="mb-8 text-gray-600">{config.description}</p>

                    {/* Home Button */}
                    <Link
                        href="/"
                        className="inline-flex items-center justify-center gap-2 rounded-lg bg-gray-900 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-gray-800"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                        >
                            <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                        </svg>
                        {t('error.goHome')}
                    </Link>
                </div>

                {/* Footer Help Text */}
                <p className="mt-8 text-sm text-gray-500">
                    {t('error.needHelp')}{' '}
                    <a
                        href="https://conquerx.freshdesk.com/support/tickets/new"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-gray-900 hover:underline"
                    >
                        {t('error.contactSupport')}
                    </a>
                </p>
            </div>
        </>
    );
};

export default ErrorPage;
